import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Filter, ChevronRight, Circle, ShoppingCart, MessageSquare, ExternalLink } from 'lucide-react';
import { Table, Tag, Input, Button, Timeline, Spin, Avatar } from 'antd';
import api from '../api/client';
import dayjs from 'dayjs';

const { Search } = Input;

const UserDetails = ({ journeyId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await api.get(`/audience/${journeyId}/events`);
        setEvents(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [journeyId]);

  if (loading) return <div className="p-4 text-center"><Spin /></div>;

  return (
    <div className="p-6 bg-slate-900/50 rounded-lg">
      <h3 className="text-white font-medium mb-4">История действий (User Journey)</h3>
      <Timeline
        mode="left"
        items={events.map(event => ({
          label: <span className="text-slate-400 text-xs">{dayjs(event.created_at).format('DD.MM.YYYY HH:mm')}</span>,
          dot: event.event_type === 'purchase' ? <ShoppingCart size={14} className="text-emerald-400" /> : 
               event.event_type === 'channel_join' ? <Users size={14} className="text-blue-400" /> :
               <Circle size={10} className="text-slate-600" />,
          color: event.event_type === 'purchase' ? 'green' : 'blue',
          children: (
            <div className="text-sm">
              <span className="text-white font-medium block mb-1">
                {event.event_type === 'channel_join' && 'Вступил в канал'}
                {event.event_type === 'channel_leave' && 'Вышел из канала'}
                {event.event_type === 'referral_click' && 'Переход по ссылке'}
                {event.event_type === 'purchase' && 'Покупка'}
                {!['channel_join', 'channel_leave', 'referral_click', 'purchase'].includes(event.event_type) && event.event_type}
              </span>
              <div className="text-slate-500 text-xs font-mono bg-black/20 p-2 rounded">
                {JSON.stringify(event.event_data)}
              </div>
            </div>
          ),
        }))}
      />
      {events.length === 0 && (
        <div className="text-slate-500 text-sm italic">Событий пока нет</div>
      )}
    </div>
  );
};

export default function Audience() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/audience');
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Пользователь',
      dataIndex: 'internal_user_id',
      key: 'user',
      render: (_, record) => (
        <div className="flex items-center gap-3">
            <Avatar style={{ backgroundColor: '#3b82f6' }}>{record.custom_attributes?.first_name?.[0] || 'U'}</Avatar>
            <div>
                <div className="font-medium text-white">
                    {record.custom_attributes?.first_name 
                        ? `${record.custom_attributes.first_name} ${record.custom_attributes.last_name || ''}` 
                        : `User #${record.journey_id}`}
                </div>
                <div className="text-xs text-slate-500 font-mono">
                    {record.custom_attributes?.username ? `@${record.custom_attributes.username}` : 'No username'}
                </div>
            </div>
        </div>
      ),
    },
    {
      title: 'Источник',
      dataIndex: 'source',
      key: 'source',
      render: (source) => (
        <Tag color="blue" className="bg-blue-500/10 text-blue-400 border-blue-500/20 capitalize">
          {source || 'organic'}
        </Tag>
      ),
    },
    {
      title: 'Дата входа',
      dataIndex: 'first_seen',
      key: 'date',
      render: (date) => <span className="text-slate-400">{dayjs(date).format('DD.MM.YYYY')}</span>,
    },
    {
      title: 'Статус',
      dataIndex: 'subscription_status',
      key: 'status',
      render: (status) => {
        const colors = { new: 'default', subscribed: 'green', customer: 'gold', churned: 'red' };
        const labels = { new: 'Новый', subscribed: 'Подписан', customer: 'Клиент', churned: 'Отписался' };
        return <Tag color={colors[status]} bordered={false}>{labels[status] || status}</Tag>;
      }
    },
    {
      title: 'LTV',
      dataIndex: 'ltv',
      key: 'ltv',
      render: (ltv) => <span className="text-emerald-400 font-medium">{ltv} ₽</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Аудитория</h1>
          <p className="text-slate-400">Сквозная аналитика по каждому пользователю</p>
        </div>
        <div className="flex gap-2">
           <Button icon={<Filter size={16} />} className="bg-surface border-white/10 text-slate-300">Фильтр</Button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-surface rounded-xl border border-white/5 overflow-hidden"
      >
        <Table
          dataSource={users}
          columns={columns}
          rowKey="journey_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          className="custom-table"
          expandable={{
            expandedRowRender: (record) => <UserDetails journeyId={record.journey_id} />,
            expandIcon: ({ expanded, onExpand, record }) => (
              <ChevronRight 
                size={16} 
                className={`text-slate-500 transition-transform cursor-pointer ${expanded ? 'rotate-90' : ''}`}
                onClick={e => onExpand(record, e)}
              />
            ),
            expandedRowKeys,
            onExpand: (expanded, record) => {
                setExpandedRowKeys(expanded ? [...expandedRowKeys, record.journey_id] : expandedRowKeys.filter(k => k !== record.journey_id));
            }
          }}
          locale={{ emptyText: <div className="py-8 text-slate-500">Нет данных о пользователях</div> }}
        />
      </motion.div>
    </div>
  );
}
